import React, { useState, useEffect } from 'react';
import { Col, Row, Button } from 'reactstrap';
import { ValidatedForm } from 'react-jhipster';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Alert, Box, Typography, IconButton, InputAdornment, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { handlePasswordResetFinish, reset } from '../password-reset.reducer';
import PasswordStrengthBar from 'app/shared/layout/password/password-strength-bar';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import theme from 'app/styles/theme/theme';
import './password-reset-finish.scss';

const PasswordInput = ({
  name,
  placeholder,
  value,
  onChange,
  show,
  toggle,
  validate,
}: {
  name: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  show: boolean;
  toggle: () => void;
  validate: any;
}) => {
  return (
    <TextField
      fullWidth
      type={show ? 'text' : 'password'}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      error={Boolean(validate(value) !== true)}
      helperText={validate(value) !== true ? validate(value) : ' '}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={toggle} edge="end">
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ mt: 1, mb: 1 }}
    />
  );
};

export const PasswordResetFinishPage = () => {
  const dispatch = useAppDispatch();
  const successMessage = useAppSelector(state => state.passwordReset.successMessage);
  const errorMessage = useAppSelector(state => state.passwordReset.errorMessage);
  const passwordResetSuccess = useAppSelector(state => state.passwordReset.resetPasswordSuccess);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(reset());
    };
  }, []);

  useEffect(() => {
    if (passwordResetSuccess) {
      setTimeout(() => {
        navigate('/');
      }, 2000);
    }
  }, [passwordResetSuccess]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
    }
  }, [successMessage]);

  const validatePassword = (value: string) => {
    if (!value) return 'Your password is required.';
    if (value.length < 8) return 'Password must be at least 8 characters.';
    if (value.length > 50) return 'Password cannot exceed 50 characters.';
    const pattern = /^(?!\s)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()])[A-Za-z\d!@#$%^&*()]{8,}$/;
    if (!pattern.test(value)) {
      return 'Password must include lowercase, uppercase, digit, and special character.';
    }
    return true;
  };

  const validateConfirmPassword = (value: string) => {
    if (!value) return 'Confirmation password is required.';
    if (value !== password) return 'Passwords do not match!';
    return true;
  };

  const handleValidSubmit = () => {
    const isPasswordValid = validatePassword(password) === true;
    const isConfirmValid = validateConfirmPassword(confirmPassword) === true;
    if (isPasswordValid && isConfirmValid) {
      dispatch(handlePasswordResetFinish({ key, newPassword: password }));
    }
  };

  const getResetForm = () => (
    <form onSubmit={e => { e.preventDefault(); handleValidSubmit(); }} className="text-center">
      <PasswordInput
        name="newPassword"
        placeholder="New password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        show={showPassword}
        toggle={() => setShowPassword(!showPassword)}
        validate={validatePassword}
      />
      <PasswordStrengthBar password={password} />
      <PasswordInput
        name="confirmPassword"
        placeholder="Confirm password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        show={showConfirmPassword}
        toggle={() => setShowConfirmPassword(!showConfirmPassword)}
        validate={validateConfirmPassword}
      />

      {errorMessage && (
        <Box sx={{ my: 1 }}>
          <Alert severity="error">
            <Typography variant="subtitle2" fontSize="12px">
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      )}

      {successMessage && (
        <Box sx={{ my: 1 }}>
          <Alert severity="success">
            <Typography variant="subtitle2" fontSize="12px">
              {successMessage.data}
            </Typography>
          </Alert>
        </Box>
      )}

      <Button
        color="success"
        type="submit"
        data-cy="submit"
        style={{
          marginBottom: '10px',
          width: theme.createActivity.width,
          height: theme.createActivity.height,
          backgroundColor: theme.palette.primary.light,
          borderRadius: theme.createActivity.borderRadius,
          color: '#ffffff',
          borderColor: theme.palette.primary.light,
        }}
      >
        Send
      </Button>
    </form>
  );

  return (
    <div style={{ height: '100vh', width: '100%' }}>
      <Row noGutters className="justify-content-center" style={{ padding: '10%' }}>
        <Col md="4" className="p-2">
          <div className="text-center py-3" onClick={() => navigate('/')}>
            <img src="content/images/logos/reachamy-logo.webp" alt="reach amy logo" style={{ height: '65px' }} />
          </div>
          <h5 className="text-center p-2">Reset password</h5>
          <div>{key ? getResetForm() : null}</div>
        </Col>
      </Row>
    </div>
  );
};

export default PasswordResetFinishPage;
